import { useEffect, useRef, useState } from 'react';
import './Landing.css';

const GATE_HASH = '3df24488867ee484cbb755ffacd1f2fbd804afddb4e7cfa2ecbf15631007ffd0';

async function sha256(text) {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function Landing({ onUnlock }) {
  const canvasRef = useRef(null);
  const inputRef = useRef(null);
  const [time, setTime] = useState('');
  const [gateOpen, setGateOpen] = useState(false);
  const [value, setValue] = useState('');
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    document.body.classList.add('landing-body');
    return () => document.body.classList.remove('landing-body');
  }, []);

  useEffect(() => {
    function tick() {
      setTime(new Date().toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true
      }));
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let raf;
    let t = 0;

    function resize() {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
    }
    resize();
    window.addEventListener('resize', resize);

    function draw() {
      const w = canvas.width;
      const h = canvas.height;
      const dpr = window.devicePixelRatio;
      ctx.clearRect(0, 0, w, h);

      const midY = h / 2;
      const breathe = 0.6 + 0.4 * Math.sin(t * 0.008);
      const amp = h * 0.055 * breathe;

      ctx.beginPath();
      for (let x = 0; x <= w; x += 2 * dpr) {
        const y = midY
          + Math.sin(x * 0.004 / dpr + t * 0.02) * amp
          + Math.sin(x * 0.011 / dpr - t * 0.013) * amp * 0.35;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(140, 255, 170, 0.10)';
      ctx.lineWidth = 1.2 * dpr;
      ctx.stroke();

      // second, fainter trace slightly out of phase — a scope with two channels
      ctx.beginPath();
      for (let x = 0; x <= w; x += 2 * dpr) {
        const y = midY
          + Math.sin(x * 0.004 / dpr + t * 0.02 + 2.4) * amp * 0.5
          + Math.sin(x * 0.017 / dpr + t * 0.01) * amp * 0.2;
        if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.045)';
      ctx.lineWidth = 1 * dpr;
      ctx.stroke();

      t += 1;
      raf = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => {
    if (gateOpen) inputRef.current?.focus();
  }, [gateOpen]);

  async function submit() {
    const hash = await sha256(value.trim().toLowerCase());
    if (hash === GATE_HASH) {
      onUnlock();
    } else {
      setDenied(true);
      setValue('');
      setTimeout(() => setDenied(false), 600);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape') { setGateOpen(false); setValue(''); }
  }

  return (
    <div className="landing">
      <canvas ref={canvasRef} className="landing-scope" />

      <div className="landing-center">
        <button
          className="landing-logo"
          onClick={() => setGateOpen(o => !o)}
          aria-label="lakeshore"
        >
          lakeshore<sup>™</sup>
        </button>

        <div className="landing-tag">
          music technology<span className="landing-cursor" />
        </div>

        <form
          className={`landing-gate ${gateOpen ? 'open' : ''}`}
          onSubmit={e => { e.preventDefault(); submit(); }}
        >
          <input
            ref={inputRef}
            type="password"
            className={denied ? 'denied' : ''}
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            spellCheck="false"
            autoComplete="off"
            aria-label="passphrase"
          />
        </form>
      </div>

      <div className="landing-corner bl">los angeles — {time}</div>
      <a className="landing-corner bc" href="mailto:abhi@lakeshore.studio">abhi@lakeshore.studio</a>
      <div className="landing-corner br"><span className="standby-led" /> stby</div>
    </div>
  );
}
